import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { OnChangeFn, RowSelectionState, SortingState, VisibilityState } from '@tanstack/react-table';
import { stableKey, useTableColumns } from './table-columns';
import { useTableInstance } from './use-table-instance';
import type {
  UseDataTableOptions,
  UseDataTableState,
  UseRequestInput,
} from './use-data-table-types';
import type { DataTableParams, DataTableRow, DataTableSort } from '@/shared/ui/data-table/types';

export type { UseDataTableOptions, UseDataTableState } from './use-data-table-types';

/**
 * Follows a controlled page without an effect.
 *
 * When the caller owns the page number, that number is the truth and the table
 * must render it in the same pass. Syncing it from an effect would draw one
 * frame of the old page and spend a second full render of every row to catch
 * up — and React already documents this conditional adjust-during-render form.
 */
function useControlledPage(
  controlledPage: number | undefined,
  setPageIndex: Dispatch<SetStateAction<number>>,
) {
  const [seenControlledPage, setSeenControlledPage] = useState(controlledPage);

  if (controlledPage !== undefined && controlledPage !== seenControlledPage) {
    setSeenControlledPage(controlledPage);
    setPageIndex(Math.max(0, controlledPage - 1));
  }
}

/** Page navigation, clamped to the pages the server can actually answer. */
function pageNavigation(
  pageCount: number,
  setPageIndex: Dispatch<SetStateAction<number>>,
): Pick<UseDataTableState<never>, 'goToPage' | 'nextPage' | 'previousPage'> {
  return {
    goToPage: (page: number) => setPageIndex(Math.max(0, Math.min(pageCount - 1, page - 1))),
    nextPage: () => setPageIndex((current) => Math.min(pageCount - 1, current + 1)),
    previousPage: () => setPageIndex((current) => Math.max(0, current - 1)),
  };
}

/**
 * Reports the selected row keys upward. The updater is applied eagerly so the
 * caller's notification and the table's own state can never disagree about
 * which rows are selected.
 */
function rowSelectionHandler(
  rowSelection: RowSelectionState,
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>,
  onSelectionChange: ((rowKeys: string[]) => void) | undefined,
): OnChangeFn<RowSelectionState> {
  return (updater) => {
    setRowSelection(updater);
    const next = typeof updater === 'function' ? updater(rowSelection) : updater;
    onSelectionChange?.(Object.keys(next));
  };
}

/**
 * The query the table wants the server to answer, as a referentially stable
 * object. A fresh identity on an unchanged query would re-fire `onFetch` on
 * every render, so each piece is keyed by content and the parts are joined in
 * one memo.
 */
function useRequestParams(input: UseRequestInput): DataTableParams {
  const filtersKey = stableKey({
    ...input.filters,
    ...input.columnFilters,
    ...(input.searchValue ? { search: input.searchValue } : {}),
  });

  const filters = useMemo(() => JSON.parse(filtersKey) as Record<string, string>, [filtersKey]);
  const sort = useMemo<DataTableSort[]>(
    () => input.sorting.map((term) => ({ id: term.id, desc: term.desc })),
    [input.sorting],
  );

  return useMemo(
    () => ({
      page: input.pageIndex + 1,
      pageSize: input.pageSize,
      sort,
      filters,
      append: input.append,
    }),
    [input.pageIndex, input.pageSize, sort, filters, input.append],
  );
}

/**
 * Owns the server-query state for a data table.
 *
 * Three things are deliberate here:
 *
 *   - **No client row models.** Sorting, filtering and pagination are all
 *     `manual`, so the table can never silently re-slice the page it was handed.
 *   - **`onFetch` is held in a ref.** The effect depends on the query, not on
 *     the callback identity, so an inline arrow does not re-query every render.
 *   - **A stale response is impossible to show.** Page changes reset to the
 *     first page, which is the only correct answer for a different sort or
 *     filter set — page 7 of a new ordering is not page 7 of the old one.
 */
/**
 * Resolves a controlled sort display into TanStack sorting state, keyed by
 * content so a caller building the array inline does not rebuild it.
 */
function useControlledSorting(sort: DataTableSort[] | undefined): SortingState | null {
  const key = sort === undefined ? null : stableKey(sort);
  return useMemo(
    () =>
      key === null
        ? null
        : (JSON.parse(key) as DataTableSort[]).map((term) => ({ id: term.id, desc: term.desc })),
    [key],
  );
}

/**
 * The sorting handler for a table.
 *
 * Uncontrolled sorting is ordinary state. A CONTROLLED sort display cannot rely
 * on the fetch effect: the caller owns the sort, so a click does not change the
 * request the effect watches. The click is reported immediately with the sort
 * the user asked for instead, and the caller's next render confirms it.
 */
function sortingChangeHandler(
  controlled: SortingState | null,
  request: DataTableParams,
  fetch: (params: DataTableParams) => void,
  setInternal: Dispatch<SetStateAction<SortingState>>,
): OnChangeFn<SortingState> {
  if (!controlled) return setInternal;
  return (updater) => {
    const next = typeof updater === 'function' ? updater(controlled) : updater;
    fetch({ ...request, sort: next.map((term) => ({ id: term.id, desc: term.desc })) });
  };
}

/**
 * Owns the server-query state for a data table.
 *
 * Three things are deliberate here:
 *
 *   - **No client row models.** Sorting, filtering and pagination are all
 *     `manual`, so the table can never silently re-slice the page it was handed.
 *   - **`onFetch` is held in a ref.** The effect depends on the query, not on
 *     the callback identity, so an inline arrow does not re-query every render.
 *   - **A stale response is impossible to show.** Page changes reset to the
 *     first page, which is the only correct answer for a different sort or
 *     filter set — page 7 of a new ordering is not page 7 of the old one.
 */
export function useDataTable<TData extends DataTableRow>(
  options: UseDataTableOptions<TData>,
): UseDataTableState<TData> {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const controlledSorting = useControlledSorting(options.sort);
  const sorting = controlledSorting ?? internalSorting;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pageIndex, setPageIndex] = useState(Math.max(0, options.defaultPage - 1));
  const pageCount = Math.max(1, Math.ceil(options.total / options.pageSize));

  // `onFetch` is held in a ref so an inline arrow does not re-query every render.
  const fetchRef = useRef(options.onFetch);
  useEffect(() => {
    fetchRef.current = options.onFetch;
  });

  useControlledPage(options.controlledPage, setPageIndex);

  const stableColumns = useTableColumns(options.columns);
  const request = useRequestParams({
    pageIndex,
    pageSize: options.pageSize,
    sorting,
    filters: options.filters,
    columnFilters: options.columnFilters,
    searchValue: options.searchValue,
    // Card view has no pager, so a page advance there is the scroll asking for
    // the next page of the SAME list and the answer appends. Table view pages,
    // so its advance replaces. Page 1 is always a fresh question either way.
    append: options.presentation === 'cards' && pageIndex > 0,
  });
  useEffect(() => {
    fetchRef.current(request);
  }, [request]);

  const onSortingChange = sortingChangeHandler(
    controlledSorting,
    request,
    options.onFetch,
    setInternalSorting,
  );

  const onRowSelectionChange = rowSelectionHandler(rowSelection, setRowSelection, options.onSelectionChange);
  const table = useTableInstance({
    data: options.data,
    columns: stableColumns,
    rowKey: options.rowKey,
    pageCount,
    pageIndex,
    pageSize: options.pageSize,
    selectable: options.selectable,
    sorting,
    columnVisibility,
    rowSelection,
    onSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange,
    enableSortingRemoval: controlledSorting === null,
  });

  return {
    table,
    pageCount,
    hasPreviousPage: pageIndex > 0,
    hasNextPage: pageIndex < pageCount - 1,
    ...pageNavigation(pageCount, setPageIndex),
    selectedRowKeys: Object.keys(rowSelection),
  };
}
